import { TZDate, tz as tzFn } from '@date-fns/tz';
import type { LatLong } from '@iheartjane/geolocation';
import type { Nullable, TimeZone } from '@iheartjane/types';
import { timezone, timezoneToken } from '@iheartjane/utils';
import { add, format, isAfter, isBefore, isSameDay } from 'date-fns';

import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';
import type {
  TimeRange,
  WorkingHours,
} from '@jane/bloom/types/schemas/roots/bloom/schedule';
import { openingAndClosing } from '@jane/bloom/utils/dates/openingAndClosing';
import { timeRangeForToday } from '@jane/bloom/utils/dates/timeRangeForToday';
import { getDistance } from '@jane/bloom/utils/geo/getDistance';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';

type WorkingHourDetails = {
  formattedHours: string;
  nextAvailableClosingTime: string | null;
  nextAvailableOpeningTime: string | null;
  openNow: boolean;
};

type GetStoresWithDetailsArgs = {
  stores: BloomMenuStore[];
  userLocation: Partial<LatLong>;
};

export function formatTime(date: Date, timezone: TimeZone) {
  const hours = format(date, 'h', { in: tzFn(timezone) });
  const minutes = format(date, 'mm', { in: tzFn(timezone) });
  const period = format(date, 'aaaaa', { in: tzFn(timezone) });

  // Only include minutes if they're not '00'
  const timeString =
    minutes === '00' ? `${hours}${period}` : `${hours}:${minutes}${period}`;

  return timeString;
}

export function formatDay(day: string) {
  if (!day) return '';
  return day.charAt(0).toUpperCase() + day.slice(1, 3);
}

export function isOpenNow(openingHours: Date, closingHours: Date, now: Date) {
  const opening = new Date(openingHours);
  const closing = new Date(closingHours);

  return isAfter(now, opening) && isBefore(now, closing);
}

function daysTillNextOpening(workingHours: WorkingHours[], now: Date) {
  let startDayIndex = now.getDay() + 1; // Starting from tomorrow
  let daysTillNextOpening = 1; // Offset from today

  // Loop until we find a day with a valid opening period or reach a full week
  while (
    workingHours[startDayIndex]?.period.from === null &&
    daysTillNextOpening < 7
  ) {
    startDayIndex = (startDayIndex + 1) % workingHours.length; // Wrap around to 0
    daysTillNextOpening++;
  }

  return daysTillNextOpening;
}

export function getNextOpeningHours(
  workingHours: WorkingHours[],
  now: Date,
  willOpenLaterToday: boolean
): {
  day: string;
  period: {
    from: string;
    to: string;
  };
} | null {
  if (!workingHours) {
    return null;
  }

  const currentWeekday = now.getDay();

  // Return current day's working hours if they will open later today
  if (willOpenLaterToday) {
    return workingHours[currentWeekday] as {
      day: string;
      period: {
        from: string;
        to: string;
      };
    };
  }

  // Check the next 6 days for an opening period
  for (let i = 1; i <= 6; i++) {
    const startDayIndex = (currentWeekday + i) % workingHours.length;
    const nextOpeningHours = workingHours[startDayIndex];
    if (nextOpeningHours?.period.from) {
      return nextOpeningHours as {
        day: string;
        period: {
          from: string;
          to: string;
        };
      };
    }
  }

  // If no opening is found, return null
  return null;
}

function getNextOpeningTime(
  workingHours: WorkingHours[],
  ianaTz: TimeZone,
  willOpenLaterToday: boolean
): WorkingHourDetails {
  const zonedNow = new TZDate(new Date(), ianaTz);

  const nextOpening = getNextOpeningHours(
    workingHours,
    zonedNow,
    willOpenLaterToday
  );

  if (!nextOpening || !nextOpening.period.from || !nextOpening.period.to) {
    return {
      formattedHours: '',
      nextAvailableClosingTime: null,
      nextAvailableOpeningTime: null,
      openNow: false,
    };
  }

  const period: { from: string; to: string } = nextOpening.period;
  const formattedDay = formatDay(nextOpening.day);

  const [nextOpeningTime, nextClosingTime] = openingAndClosing(
    period,
    ianaTz,
    add(zonedNow, {
      days: daysTillNextOpening(workingHours, zonedNow),
    })
  );

  const nextOpeningTimeFormatted = formatTime(nextOpeningTime, ianaTz);

  const nextAvailableOpeningTime = willOpenLaterToday
    ? formatTime(nextOpeningTime, ianaTz)
    : format(nextOpeningTime, 'MM/dd');

  return {
    formattedHours: `Closed until ${formattedDay}, ${nextOpeningTimeFormatted} ${timezoneToken(
      ianaTz
    )}`,
    nextAvailableClosingTime: formatTime(nextClosingTime, ianaTz),
    nextAvailableOpeningTime,
    openNow: false,
  };
}

function workingHourDetails(
  timeRange: Nullable<TimeRange>,
  tz: TimeZone,
  workingHours: WorkingHours[]
): WorkingHourDetails {
  const ianaTz = timezone(tz);
  const zonedNow = new TZDate(new Date(), ianaTz);

  let opening: Date | undefined;
  let closing: Date | undefined;

  if (timeRange && timeRange?.from && timeRange?.to) {
    [opening, closing] = openingAndClosing(
      timeRange as { from: string; to: string },
      tz
    );
  }

  const willOpenLaterToday =
    opening && isAfter(opening, zonedNow) && isSameDay(opening, zonedNow);

  if (opening && closing && isOpenNow(opening, closing, zonedNow)) {
    return {
      formattedHours: `Open until ${formatTime(closing, ianaTz)} ${timezoneToken(ianaTz)}`,
      nextAvailableClosingTime: null,
      nextAvailableOpeningTime: null,
      openNow: true,
    };
  }

  return getNextOpeningTime(workingHours, ianaTz, !!willOpenLaterToday);
}

export function getStoresWithDetails({
  stores,
  userLocation,
}: GetStoresWithDetailsArgs): StoreViewModel[] {
  return stores.map((store) => {
    const todaysHours = timeRangeForToday(
      store.working_hours,
      store.time_zone_identifier
    );

    let distance;
    const d = getDistance(
      userLocation?.lat,
      userLocation?.long,
      store.latitude,
      store.longitude
    );
    if (isNaN(d)) {
      distance = '';
    } else {
      distance = `${d.toFixed(d > 20 ? 0 : 1)}mi`;
    }

    return {
      ...store,
      ...workingHourDetails(
        todaysHours,
        store.time_zone_identifier,
        store.working_hours || []
      ),
      distance,
    };
  });
}
